import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { Flag, Pencil } from 'lucide-react';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface FlagsSectionProps {
  /** Opens the full-screen Flags picker (Flutter `flags_selection_widget`). */
  onEdit?: () => void;
}

/**
 * FlagsSection — bulleted-text card matching Flutter
 * `create_appointment_widget.dart:1681–1742` + `flags_widget.dart`.
 *
 * Source of truth: form's `patientFlags` (auto-populated from
 * PreBookSelectedPatient and edited via the picker). Flutter shows the
 * same bulleted line with a pencil to open the picker; Save in the
 * picker writes back to this list.
 */
export function FlagsSection({ onEdit }: FlagsSectionProps) {
  const { watch } = useFormContext<AppointmentFormValues>();
  const patientFlags = watch('patientFlags');

  // Render-side dedupe — defensive against any path that bypasses the
  // patient-section's dedupe (e.g. a future form-level reset).
  const uniqueDisplays = useMemo(() => {
    if (!patientFlags?.length) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const f of patientFlags) {
      const display = (f.display ?? '').trim();
      const key = display.toLowerCase();
      if (!display || seen.has(key)) continue;
      seen.add(key);
      out.push(display);
    }
    return out;
  }, [patientFlags]);

  if (uniqueDisplays.length === 0) return null;

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Flags
      </p>
      <div className="flex items-start gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
        <Flag className="h-5 w-5 text-brand-primary flex-shrink-0 mt-1" />
        <p className="flex-1 text-sm text-text-primary leading-relaxed">
          {uniqueDisplays.join(' • ')}
        </p>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
            aria-label="Edit flags"
          >
            <Pencil className="h-4 w-4 text-brand-primary" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
